import React, { useState } from "react";

//UI Material
import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  SwipeableDrawer,
  Typography,
} from "@mui/material";

//Material Icons
import { Add, Cancel, Inventory2 } from "@mui/icons-material";

//Local components
import Form from "../basics/Form";
import ModalWindow from "../Modal";

//Helpers
import { fetchWithTokenJSONPost } from "../../Helpers/network";
import { currencies } from "../../Constants/currencies";

const reasonsDictionary = [
  "Salida de stock",
  "Inventario repetido",
  "Inventario perdido",
  "Inventario dañado",
  "Ajuste por error de conteo",
  "Producto vencido",
  "Producto obsoleto",
  "Donación de inventario",
  "Uso interno",
  "Muestra para clientes",
  "Robo o hurto",
  "Devolución no reutilizable",
  "Transferencia a otra bodega",
  "Inventario en mal estado por condiciones de almacenamiento",
  "Desperdicio o merma",
  "Reciclaje o disposición final",
];

export default function NewItemStockoutDrawer({
  data = {},
  open,
  setOpen,
  getData,
}) {
  // ==========>Component States<==========
  //State for item stockout data
  const [itemStockoutData, setItemStockoutData] = useState({});
  //State for setting loader
  const [isLoading, setIsLoading] = useState(false);
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);

  const addStockoutForm = [
    {
      selection: "textfield",
      id: "quantity",
      initialValue: itemStockoutData.quantity,
      xs: 6,
      md: 3,
      label: "Cantidad de ítems",
      multiline: false,
      type: "number",
      inputProps: {},
      placeholder: "Cantidad de este ítem",
      content: [],
      style: {},
    },
    {
      selection: "textfield",
      id: "costPerSoldUnit",
      initialValue:
        data.estimatedPriceOfSale || itemStockoutData.costPerSoldUnit,
      xs: 6,
      md: 3,
      label: "Costo unitario de venta (opcional)",
      multiline: false,
      type: "number",
      inputProps: {},
      placeholder: "Costo por unidad",
      content: [],
      style: {},
    },
    {
      selection: "selector",
      id: "currency",
      initialValue: itemStockoutData.currency || 0,
      xs: 6,
      md: 3,
      label: "Tipo de moneda",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "",
      content: currencies,
      style: { minWidth: "100%" },
    },
    {
      selection: "textfield",
      id: "taxesPercentage",
      initialValue: itemStockoutData.taxesPercentage || 13,
      xs: 6,
      md: 3,
      label: "Porcentaje por impuestos (opcional)",
      multiline: false,
      type: "number",
      inputProps: {
        endAdornment: <InputAdornment>%</InputAdornment>,
      },
      placeholder: "Porcentaje por impuestos",
      content: [],
      style: {},
    },
    {
      selection: "selector",
      id: "taxesIncluded",
      initialValue: itemStockoutData.taxesIncluded || 0,
      xs: 12,
      md: 4,
      label: "¿Los impuestos están incluídos en el costo unitario de venta?",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "",
      content: ["Sí", "No"],
      style: { minWidth: "100%" },
    },
    {
      selection: "selector",
      id: "reason",
      initialValue: itemStockoutData.reason || 0,
      xs: 12,
      md: 4,
      label: "Razón de salida",
      multiline: false,
      type: null,
      inputProps: {},
      placeholder: "",
      content: reasonsDictionary,
      style: { minWidth: "100%" },
    },
    {
      selection: "textfield",
      id: "additionalInfo",
      initialValue: itemStockoutData.additionalInfo,
      xs: 12,
      md: 4,
      label: "Información adicional (opcional)",
      multiline: false,
      type: "text",
      inputProps: {},
      placeholder: "Información adicional",
      content: [],
      style: {},
    },
  ];

  // ==========> Component Functions <==========
  //Function to create new item entry
  const setItemStockout = async () => {
    setIsLoading(true);

    const newItemStockout = {
      id: data.id,
      name: data.name,
      quantity: Number(itemStockoutData.quantity),
      costPerSoldUnit: itemStockoutData.costPerSoldUnit,
      currency: currencies[itemStockoutData.currency],
      taxesPercentage: itemStockoutData.taxesPercentage,
      taxesIncluded: itemStockoutData.taxesIncluded === 0,
      reason: reasonsDictionary[itemStockoutData.reason],
      additionalInfo: itemStockoutData.additionalInfo,
    };

    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/inventory/create-item-stockout",
        newItemStockout
      );
      const jsonResponse = await requestResponse.json();

      if (jsonResponse.status === "ok") {
        setModalText("Salida de ítem registrada exitosamente.");
        setModalDescription(jsonResponse.description);
        getData();
        setOpen(false);
      } else {
        setModalText("No se pudo realizar la acción.");
        setModalDescription(jsonResponse.description);
      }
    } catch (error) {
      console.log(error);
    }
    setModalOpen(true);
    setIsLoading(false);
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      transitionDuration={600}
      disableSwipeToOpen={true}
      open={open}
      sx={{
        zIndex: "99",
        "& .MuiDrawer-paper": {
          justifyContent: "center",
          borderRadius: "35px 35px 0px 0px",
        },
      }}
      onClose={() => setOpen(false)}
      onOpen={() => {
        /**empty arrow function */
      }}
    >
      <Grid
        container
        justifyContent="center"
        sx={{ padding: "15px" }}
        textAlign="center"
        spacing={1}
      >
        <Grid item xs={12} sx={{ marginTop: "10px" }}>
          <Inventory2 color="error" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h7" fontWeight="bold" color="error">
            {`Nueva salida de ítem`}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight="bold">
            {data.name}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">{`Identificador: ${data.id}`}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Form
            data={addStockoutForm}
            handleStateChange={setItemStockoutData}
          />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="warning" variant="filled">
            Por favor asegúrese de que la información que ingresó sobre el costo
            unitario de venta y los impuestos esté correcta. Esta información
            será utilizada para el análisis de costos del inventario.
          </Alert>
        </Grid>

        <Grid item xs={12} sx={{ marginTop: "10px" }}>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              size="small"
              variant="contained"
              color="error"
              sx={{ color: "white" }}
              endIcon={<Cancel />}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancelar
            </Button>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                sx={{ color: "white" }}
                endIcon={<Add />}
                disabled={itemStockoutData.name === ""}
                onClick={setItemStockout}
              >
                Registrar salida
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
      <ModalWindow
        isOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={modalText}
        text={modalDescription}
        leftText="OK"
        leftClick={() => {
          setModalOpen(false);
        }}
      />
    </SwipeableDrawer>
  );
}
